import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import SupervisorPinModal from "@/components/SupervisorPinModal";
import KnifeAreaCard from "@/components/knives/KnifeAreaCard";
import KnifeCountDialog from "@/components/knives/KnifeCountDialog";
import KnifeExpectedSettings from "@/components/knives/KnifeExpectedSettings";
import KnifeHistory from "@/components/knives/KnifeHistory";

export default function KnifeCount() {
  const { user } = useAuth();
  const [data, setData] = useState({ shift_date: "", areas: [] });
  const [target, setTarget] = useState(null);
  const [signFor, setSignFor] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    const { data: d } = await api.get("/knives/today");
    setData(d);
    setRefreshKey((k) => k + 1);
  }, []);
  useEffect(() => { load(); }, [load]);

  const signoff = async (_who, note, pin) => {
    try {
      await api.post(`/knives/counts/${signFor.id}/signoff`, { pin, note });
      toast.success("Signed off");
      setSignFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-off failed");
    }
  };

  const configured = data.areas.some((a) => a.expected > 0);
  const endDue = data.areas.filter((a) => a.expected > 0 && !a.end);

  return (
    <div className="space-y-6">
      <PageHeader title="Knife Count" eyebrow={`Production · Shift ${data.shift_date}`} />
      {!configured && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="knife-not-configured">
          No expected knife numbers set yet. {user?.role === "admin" ? "Set them below." : "Ask an admin to set them."}
        </div>
      )}
      {endDue.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4" data-testid="knife-end-reminder">
          <BellRing className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900 dark:text-amber-100">
            <div className="font-semibold">End-of-shift knife count still due in {endDue.length} area{endDue.length !== 1 ? "s" : ""}</div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {endDue.map((a) => (
                <button key={a.location_id} onClick={() => setTarget({ area: a, shift: "end" })}
                  className="rounded-full bg-white dark:bg-stone-900 border border-amber-300 px-3 h-8 text-xs font-semibold hover:bg-amber-100"
                  data-testid={`knife-reminder-${a.location_id}`}>
                  {a.name}{a.start ? "" : " (no start count)"}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="knife-areas">
        {data.areas.map((a) => (
          <KnifeAreaCard key={a.location_id} area={a} onCount={(area, shift) => setTarget({ area, shift })} onSignoff={setSignFor} />
        ))}
      </div>
      <KnifeHistory refreshKey={refreshKey} />
      {user?.role === "admin" && <KnifeExpectedSettings areas={data.areas} onSaved={load} />}
      <KnifeCountDialog target={target} onClose={() => setTarget(null)} onSaved={() => { setTarget(null); load(); }} />
      <SupervisorPinModal
        open={!!signFor}
        onClose={() => setSignFor(null)}
        onVerified={signoff}
        roles={["admin"]}
        title="Supervisor sign-off"
        description={signFor ? `${signFor.location_name}: ${signFor.counted} of ${signFor.expected} knives. Enter supervisor PIN to sign off.` : ""}
        noteLabel="Action taken (optional)"
      />
    </div>
  );
}
